import moment from 'moment';
import React, {
  forwardRef,
  RefCallback,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { List } from '../../../../components/List';
import { Popover } from '../../../../components/Popover';
import { TextInput } from '../../../../components/TextInput';
import { identifyComponent } from '../../../../util';
import { DateRangePickerContext } from '../../context';
import { FocusPosition, RangeIndex } from '../../types';
import { PARSE_FORMATS } from '../DateInput/constants';
import css from './index.module.scss';
import { useTimeOptions } from './useTimeOptions';

const validateTime = (input: string) => {
  if (!input) {
    // and empty time is also valid (because we'll just reset it)
    return true;
  }
  // validate the time input on an arbitrary date with moment
  const isDateValid = moment(
    `${new Date().toLocaleDateString()} ${input}`,
    PARSE_FORMATS,
  ).isValid();
  return isDateValid;
};

interface TimeInputProps {
  time: string;
  onFocus: () => void;
  onFocusNext: () => void;
  onChange: (time: string, preventNext?: boolean) => void;
  rangeKey: RangeIndex;
  positionKey: FocusPosition;
}

export const TimeInput = forwardRef<{ blur?: () => void; focus: () => void }, TimeInputProps>(
  ({ time, onFocus, onChange, rangeKey, positionKey }, ref) => {
    const { readOnly } = useContext(DateRangePickerContext);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputTime, setInputTime] = useState(time);
    const [open, setOpen] = useState(false);
    // use this to only scroll once per open
    const scrolledRef = useRef(false);
    const setOpenCb = useCallback((v: boolean) => {
      setOpen(v);
      if (!v) {
        // reset scroll ref on close
        scrolledRef.current = false;
      }
    }, []);
    const setTime = useCallback(
      (time: string) => {
        setInputTime(time);
        onChange(time);
      },
      [onChange],
    );

    const { timeOptions, timeOptionRefs } = useTimeOptions(
      inputTime,
      setOpenCb,
      setTime,
      rangeKey,
      positionKey,
    );

    useEffect(() => {
      // if a new time is passed in, overwrite local state
      setInputTime(time);
    }, [time]);

    useImperativeHandle(
      ref,
      () => ({
        blur: () => {
          inputRef.current?.blur();
        },
        focus: () => {
          inputRef.current?.focus();
        },
      }),
      [],
    );

    const renderListRef: RefCallback<HTMLUListElement> = () => {
      if (scrolledRef.current || !open || !inputTime) return;
      // scrolls to nearest time option if inputTime is set
      const inputTimeToday = moment(
        `${new Date().toLocaleDateString()} ${inputTime}`,
        PARSE_FORMATS,
      );
      const r = 15 - (inputTimeToday.minute() % 15);
      const nearestTime = inputTimeToday
        .add(r, 'minutes')
        .second(0)
        .millisecond(0)
        .format('hh:mm A');
      const index = timeOptions.findIndex(o => o.timeOption === nearestTime);
      if (index !== -1 && timeOptionRefs.current[index]) {
        requestAnimationFrame(() => {
          timeOptionRefs.current[index]?.scrollIntoView({ block: 'nearest' });
          scrolledRef.current = true;
        });
      }
    };

    return (
      <>
        <TextInput
          data-component="DateInput"
          ref={inputRef}
          value={inputTime}
          onFocus={() => {
            onFocus();
            !readOnly && setOpen(true);
          }}
          onKeyDown={evt => {
            if (evt.key === 'Enter' || evt.key === 'Tab') {
              const valid = validateTime(inputTime);
              if (valid) {
                if (evt.key === 'Enter') {
                  evt.preventDefault();
                  evt.stopPropagation();
                }
                onChange(inputTime, evt.key === 'Tab');
                setOpenCb(false);
              }
            }
          }}
          onChange={() => {
            // allow the input of precise times (to the minute) but wait to validate on blur
            setInputTime(inputRef.current?.value ?? '');
          }}
          size="small"
          invalid={!validateTime(inputTime)}
          testId="date-range-picker-date-input"
          {...identifyComponent('time-input')}
          className={css.timeInput}
          disabled={readOnly}
        />
        <Popover
          anchor={inputRef.current}
          offset={8}
          open={open}
          placement="bottom-start"
          variant="light"
          maxHeight={340}
          overflow="auto"
          onCloseSignal={() => {
            const currentTime = inputRef.current?.value || inputTime;
            const isValid = validateTime(currentTime);
            if (isValid) {
              setInputTime(currentTime);
              onChange(currentTime, true);
            }
            setOpenCb(false);
          }}
          testId="time-input-popover"
        >
          <List ref={renderListRef}>{timeOptions.map(({ menuItem }) => menuItem)}</List>
        </Popover>
      </>
    );
  },
);

TimeInput.displayName = 'TimeInput';
